import { Tracing } from "@tento-core/tracing";
import { CellHeight, MeasuredHeightPx } from "@tento-ui/components/sheet/cell-height";
import { SheetCell } from "@tento-ui/components/sheet/sheet-cell";
import { SettleDebt } from "@tento-scroll/engine/scroll-engine";
import { type SheetRevealInstrumentation } from "../instrumentation.ts";
import { SheetPresentation } from "./sheet-presentation.ts";
import { SheetColumn } from "./sheet-column.ts";

/**
 * The reveal-contract predicate a presentation ticket gates on (see
 * `SheetElement`'s presentation): whether `host`'s visible strip is bound,
 * rendered and laid out at the heights its view model planned. It reads only
 * `host` (a `SheetElement` passes its own state; a harness passes a host of
 * its own, `sheetRevealReadyOf`), and writes back the strip it judged
 * (`host.presentationStrip`).
 */
export function revealReady(host: SheetRevealInstrumentation.Host): boolean {
  // An explicit null view is a legitimate empty shell, unlike an incoming
  // non-null view whose geometry has never been installed.
  if (host.sheet == null) return true;
  if (!host.columns.geometryBound) return false;
  const queue = host.bindQueue();
  const debt: SettleDebt = host.scrubbedAtBindGeneration == null
    ? { kind: "this-bind", generation: queue.current }
    : { kind: "incurred-at", generation: host.scrubbedAtBindGeneration };
  const at = SettleDebt.against(debt, queue);
  if (at.pending || at.owed) return false;
  const currentBind = SettleDebt.against({ kind: "this-bind", generation: queue.current }, queue);
  if (currentBind.owed) return false;
  // A host that is not rendered has no heights to judge: every cell of a
  // detached or `display: none` sheet measures 0, which the sampling below
  // would read as dead columns and escalate as a reveal-contract breach
  // (ux corpus 8fa8bd: all 22x2 columns, header cells that measured 77px
  // seconds earlier included, went to 0px together at gen0 with no scrub —
  // a collapsed host during a click, not a rebind defect). Hold the reveal;
  // a later drain on a rendered host decides. Same zero-box rule as the
  // size gate (`onSizeChange`, `seedSizeFromMeasurement`).
  if (!host.isConnected || Math.round(host.offsetWidth) <= 0 || Math.round(host.offsetHeight) <= 0) {
    const rect = host.getBoundingClientRect();
    const hidden = {
      gen: host.revealGeneration,
      connected: host.isConnected,
      rect: [Math.round(rect.left), Math.round(rect.top), Math.round(rect.width), Math.round(rect.height)],
      visibility: document.visibilityState,
    };
    Tracing.info("ui")("sheet/reveal-hidden-host", hidden);
    Tracing.log("sheet/reveal-hidden-host", hidden);
    return false;
  }
  const strip = host.columns.visibleIndices();
  const gen = host.revealGeneration;
  if (host.presentationStrip && CellHeight.decideRestrip(host.presentationStrip, strip).kind === "rearm") {
    Tracing.log("sheet/reveal-restrip", { gen, from: host.presentationStrip, to: strip, via: host.presentationVia });
  }
  host.recordStrip(strip);
  const sampled: CellHeight.RevealColumn[] = [];
  const measuredHeights = new Map<SheetCell, MeasuredHeightPx>();
  for (const el of host.columns.renderedElements()) {
    const columns = [el.$header, el.$body, el.$footer].filter(
      (c): c is SheetColumn => c instanceof SheetColumn,
    );
    for (const $column of columns) {
      sampled.push({
        index: el.column.inner?.index ?? null,
        cells: $column.$cells().map(($cell) => {
          const hidden =
            $cell.hasAttribute("hidden") ||
            $cell.style.display === "none";
          const measured = hidden ? MeasuredHeightPx.of(0) : $cell.measuredHeight();
          measuredHeights.set($cell, measured);
          return {
            hidden,
            // A hidden cell legitimately measures 0 and the predicate
            // excludes it, so do not pay a layout read for it.
            measured,
          };
        }),
      });
    }
  }
  const dead = CellHeight.deadColumns(sampled);
  if (dead.deadColumns > 0 && strip != null) {
    // The strip decided above, NOT a third live read: the inside /
    // flank split has to be made against the strip this reveal
    // actually judged, or the re-read it just paid for decides
    // nothing.
    const visible = strip;
    const rendered = host.columns.renderedIndices();
    // A sample that was not a height at all (NaN / ±Infinity /
    // negative) is counted DEAD rather than alive, but the reason it
    // died is different in kind from a 0px cell, so it is named
    // rather than folded into the count above.
    const unusable = dead.invalid > 0
      ? ` — ${dead.invalid} of the samples were not heights (NaN/±Infinity/negative) and count as dead`
      : "";
    // WHERE the dead columns are decides how loud this is. Inside the
    // visible strip it is the contract breach this check exists for
    // and it escalates. Only in the rendered flanks it is the
    // cosmetic class the breadcrumb's ranges were designed to tell
    // apart: a config flip legitimately leaves flank columns
    // un-laid-out at the instant the strip is ready, and the sweep
    // heals them — the cached read never saw this because the cache
    // still held the previous layout's heights.
    const where = CellHeight.partitionDead(dead.deadIndices, visible);
    const payload = {
      gen,
      deadColumns: dead.deadColumns,
      deadCells: dead.deadCells,
      deadIndices: dead.deadIndices,
      deadVisible: where.inside.length,
      deadFlank: where.flank.length,
      visibleStart: visible.start,
      visibleEnd: visible.end,
      renderedStart: rendered.start,
      renderedEnd: rendered.end,
      // Which of the three notions of height decided this. The label
      // and every key above are read by tracked specs, so the change
      // of AUTHORITY is additive rather than a renamed payload.
      source: "measured",
      // Additive, same reason: how many of those measurements could
      // not be trusted. A check that pays for layout is worth
      // nothing if it cannot say the layout read came back garbage.
      invalidMeasurements: dead.invalid,
    };
    if (where.inside.length > 0) {
      // Only a scrub can leave heights "never rebound"; without one the
      // cells simply measured zero on a host that IS rendered.
      const cause = host.scrubbedAtBindGeneration != null
        ? "scrub heights were never rebound"
        : "the cells measured zero on a rendered host";
      Tracing.err("ui")(
        `sheet/reveal-contract: holding reveal with ${dead.deadColumns} dead column(s) (${dead.deadCells} zero-height bound cells), ${where.inside.length} inside the visible strip — ${cause}${unusable}`,
      );
      Tracing.log("sheet/reveal-contract", payload);
    } else {
      // Cosmetic: nothing the user is about to see is dead. Logged
      // under its own label so the reveal-contract forbid keeps
      // meaning "a breach", and the flank census stays countable.
      Tracing.log("sheet/reveal-flank", payload);
    }
  }
  const entries = host.columns.renderedElements();
  const visibleEntries = entries.filter((entry) => {
    const index = entry.column.inner?.index;
    return index != null && index >= strip.start && index <= strip.end;
  });
  if (visibleEntries.length < strip.end - strip.start + 1) return false;
  for (const entry of visibleEntries) {
    for (const $column of [entry.$header, entry.$body, entry.$footer]) {
      if (!$column) continue;
      if (["pending-layout", "recycled", "pooled", "bind-failed"].some((attr) => $column.hasAttribute(attr))) return false;
    }
  }
  const plannedHeights = new Map([...host.sheet.layout.rowLayout].map(([row, layout]) => [String(row), layout.cellHeightPx]));
  for (const entry of visibleEntries) {
    for (const $column of [entry.$header, entry.$body, entry.$footer]) {
      if (!($column instanceof SheetColumn)) continue;
      for (const $cell of $column.$cells()) {
        const hidden = $cell.hasAttribute("hidden") || $cell.style.display === "none";
        const spanHidden = $cell.getAttribute("data-span-mode") === "hidden";
        if (!SheetPresentation.geometryReady({
          hidden, spanHidden,
          plannedHeight: plannedHeights.get($cell.getAttribute("rid") ?? "") ?? null,
          measuredHeight: measuredHeights.get($cell) ?? 0,
        })) return false;
      }
    }
  }
  return true;
}

